import React, { useState } from "react";
import { Badge, Button, Card, Dialog, Flex, Switch, Text } from "@radix-ui/themes";
import { useQueryClient } from "@tanstack/react-query";

import {
  useDeactivateEatApp,
  useGetEatAppStatus,
  useSyncEatApp,
  useUpdateEatAppPullSettings,
} from "hooks";
import { useProfileContext } from "context";
import { useToast } from "../../../context/ToastContext";
import { EatAppMark } from "./EatAppMark";
import { EatAppSetupDialog } from "./EatAppSetupPage";
import { EatAppMappingDialog } from "./EatAppMappingDialog";
import { PermissionAlertDialog } from "components";

export function ActiveEatAppIntegration() {
  const [setupOpen, setSetupOpen] = useState(false);
  const [setupConfig, setSetupConfig] = useState(null);
  const [mappingConfig, setMappingConfig] = useState(null);
  const [disconnectConfig, setDisconnectConfig] = useState(null);
  const [permissionOpen, setPermissionOpen] = useState(false);
  const queryClient = useQueryClient();
  const { userProfile } = useProfileContext();
  const { setToastMessage } = useToast();
  const organisationId = userProfile?.organisation_id;
  const { data } = useGetEatAppStatus(organisationId);
  const updatePull = useUpdateEatAppPullSettings();
  const deactivate = useDeactivateEatApp();
  const sync = useSyncEatApp();
  const details = data?.data || {};
  const configs = details.configs || [];

  const openSetup = (config = null) => {
    if (userProfile?.user_role === "Owner") {
      setSetupConfig(config);
      setSetupOpen(true);
    }
    else setPermissionOpen(true);
  };

  const openMappings = (config) => {
    if (userProfile?.user_role === "Owner") setMappingConfig(config);
    else setPermissionOpen(true);
  };

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["eat-app-status"] });

  const changePull = async (configId, checked) => {
    try {
      await updatePull.mutateAsync({ organisation_id: organisationId, config_id: configId, pull_enabled: checked });
      refresh();
      setToastMessage(`Inbound Eat App sync ${checked ? "enabled" : "disabled"}.`);
    } catch (error) {
      setToastMessage(error?.response?.data?.message || "Could not update inbound sync.");
    }
  };

  const openDisconnect = (config) => {
    if (userProfile?.user_role === "Owner") setDisconnectConfig(config);
    else setPermissionOpen(true);
  };

  const disconnect = async () => {
    if (!disconnectConfig) return;
    try {
      await deactivate.mutateAsync({ organisationId, configId: disconnectConfig.config_id });
      refresh();
      setToastMessage("Eat App disconnected.");
      setDisconnectConfig(null);
    } catch (error) {
      setToastMessage(error?.response?.data?.message || "Could not disconnect Eat App.");
    }
  };

  const syncNow = async (configId) => {
    try {
      await sync.mutateAsync({ organisationId, configId });
      setToastMessage("Eat App synchronization was queued.");
    } catch (error) {
      setToastMessage(error?.response?.data?.message || "Could not start synchronization.");
    }
  };

  return (
    <>
    <Flex justify="end" mb="3">
      <Button variant="soft" onClick={() => openSetup()}>Connect another Eat App group</Button>
    </Flex>
    {configs.map((config) => (
    <Card key={config.config_id} size="3" style={{ width: "100%", marginBottom: 16 }}>
      <Flex direction="column" gap="4">
        <Flex justify="between" align="center">
          <Flex align="center" gap="3">
            <EatAppMark />
            <Flex direction="column">
              <Text size="4" weight="medium">Eat App</Text>
              <Text size="2" color="gray">Concierge group {config.group_id} · {config.environment}</Text>
            </Flex>
          </Flex>
          <Badge color={config.is_active ? "green" : "gray"}>{config.is_active ? "Connected" : "Inactive"}</Badge>
        </Flex>

        <Flex direction="column" gap="2">
          {(config.location_mappings || []).map((mapping) => (
            <Flex key={mapping.bconnect_location_id} justify="between">
              <Text>{mapping.bconnect_location_name}</Text>
              <Text color="gray">{mapping.restaurant_name || mapping.restaurant_id}</Text>
            </Flex>
          ))}
        </Flex>

        <Flex justify="between" align="center">
          <Flex direction="column">
            <Text weight="medium">Import Eat App guests into BConnect</Text>
          </Flex>
          <Switch checked={Boolean(config.pull_enabled)} onCheckedChange={(checked) => changePull(config.config_id, checked)} />
        </Flex>

        <Flex gap="3" justify="end">
          {config.is_active && (
            <Button variant="soft" color="gray" onClick={() => openMappings(config)}>Edit locations</Button>
          )}
          {!config.is_active && (
            <Button variant="soft" color="gray" onClick={() => openSetup(config)}>Finish setup</Button>
          )}
          <Button variant="soft" onClick={() => syncNow(config.config_id)} loading={sync.isPending} disabled={!config.is_active}>Sync now</Button>
          <Button variant="soft" color="red" onClick={() => openDisconnect(config)} disabled={!config.is_active}>Disconnect</Button>
        </Flex>
      </Flex>
    </Card>
    ))}
    <EatAppSetupDialog isOpen={setupOpen} onOpenChange={setSetupOpen} existingConfig={setupConfig} />
    <EatAppMappingDialog
      isOpen={Boolean(mappingConfig)}
      onOpenChange={(open) => { if (!open) setMappingConfig(null); }}
      organisationId={organisationId}
      config={mappingConfig}
    />
    <Dialog.Root open={Boolean(disconnectConfig)} onOpenChange={(open) => { if (!open) setDisconnectConfig(null); }}>
      <Dialog.Content size="3" style={{ maxWidth: 450 }}>
        <Dialog.Title>Disconnect Eat App?</Dialog.Title>
        <Dialog.Description size="2" mb="4">
          Guest synchronization will stop for group {disconnectConfig?.group_id}. Its location mappings and sync positions will be kept for reconnection.
        </Dialog.Description>
        <Flex gap="3" justify="end">
          <Dialog.Close>
            <Button variant="soft" color="gray" disabled={deactivate.isPending}>Cancel</Button>
          </Dialog.Close>
          <Button variant="solid" color="red" onClick={disconnect} loading={deactivate.isPending} disabled={deactivate.isPending}>
            Disconnect
          </Button>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
    <PermissionAlertDialog
      isOpen={permissionOpen}
      onOpenChange={setPermissionOpen}
      message="Only owners can perform this action"
    />
    </>
  );
}
